import dayjs from 'dayjs';

import { IconType } from '@/types/icon.types';
import { getTzTimeNow, isDifferenceMoreThanNDays } from '@/common/utils/time.utils';
import type { MenuInfo } from '@/common/components/organisms/baseMenu/types';
import {
  MenuSearchRecentItem,
  MenuSearchFavoriteDashboardItem,
} from '@/common/components/organisms/menuSearch/menuSearch.type';
import { EXCLUDED_MENU_VALUES } from '@/common/components/organisms/menuSearch/menuSearch.define';

import { getMenuSearchItemIcon, lookupSubTitle } from './menuSearch.common.utils';
import { IntegratedDashboardV8Item } from '@/api-client/metav8Over/model';

/**
 * MenuInfo 배열에서 특정 path를 가진 item을 재귀적으로 찾음
 * @param items - 검색할 MenuInfo 배열
 * @param targetPath - 찾을 path
 * @returns 찾은 MenuInfo 또는 null
 */
export const findMenuInfoByPath = (items: MenuInfo[], targetPath: string): MenuInfo | null => {
  for (const item of items) {
    // 현재 아이템의 path가 targetPath와 일치하면 반환
    if (item.path === targetPath) {
      return item;
    }

    // children에서 재귀적으로 검색
    if (item.children && item.children.length > 0) {
      const found = findMenuInfoByPath(item.children, targetPath);
      if (found) return found;
    }

    // subMenuList에서 재귀적으로 검색
    if (item.subMenuList && item.subMenuList.length > 0) {
      const found = findMenuInfoByPath(item.subMenuList, targetPath);
      if (found) return found;
    }
  }

  return null;
};

/**
 * 최상위 부모 MenuInfo를 찾음 (icon 추출용)
 * @param items - 검색할 MenuInfo 배열
 * @param targetPath - 찾을 path
 * @returns 최상위 부모 MenuInfo 또는 null
 */
const findRootMenuInfo = (items: MenuInfo[], targetPath: string): MenuInfo | null => {
  for (const item of items) {
    // 현재 아이템의 path가 targetPath와 일치하면 현재 아이템이 최상위
    if (item.path === targetPath) {
      return item;
    }

    // children에서 검색
    if (item.children && item.children.length > 0) {
      const found = findMenuInfoByPath(item.children, targetPath);
      if (found) return item; // 자식에서 찾았으면 현재 아이템이 최상위
    }

    // subMenuList에서 검색
    if (item.subMenuList && item.subMenuList.length > 0) {
      const found = findMenuInfoByPath(item.subMenuList, targetPath);
      if (found) return item; // 자식에서 찾았으면 현재 아이템이 최상위
    }
  }

  return null;
};

export const appendQueryString = (basePath: string, queryString?: string) => {
  if (!queryString) return basePath;

  const trimmed = queryString.trim();
  if (!trimmed) return basePath;

  const normalized = trimmed.replace(/^[?&]+/, '');
  if (!normalized) return basePath;

  return basePath.includes('?') ? `${basePath}&${normalized}` : `${basePath}?${normalized}`;
};

/**
 * 최근 조회 데이터를 MenuSearchRecentItem 배열로 변환
 * 중복된 메뉴를 구분하기 위해 index를 포함한 unique id 생성
 * 대시보드 뷰 URL(/dashboard/view/{id})인 경우 dashboardList에서 매칭
 * @param recentData - 최근 조회 데이터 ({ url?, menu?, queryString? }[])
 * @param menuInfo - 전체 메뉴 정보
 * @param dashboardList - 대시보드 목록
 * @param pathToSubTitleMap - path → subTitle 매핑 (lookup 용)
 * @returns MenuSearchRecentItem 배열
 */
export const mapRecentDataToMenuItems = ({
  t,
  recentData,
  menuInfo,
  dashboardList: dashboardList = [],
  pathToSubTitleMap,
}: {
  t?: (key: string) => string;
  recentData: Array<{ url?: string; menu?: string; queryString?: string }>;
  menuInfo: MenuInfo[];
  dashboardList: IntegratedDashboardV8Item[];
  pathToSubTitleMap?: Map<string, string>;
}): MenuSearchRecentItem[] => {
  return recentData.reduce<MenuSearchRecentItem[]>((acc, recentItem, index) => {
    // url이 없으면 건너뜀
    if (!recentItem.url) {
      return acc;
    }

    // 대시보드 뷰 URL인지 확인 (/dashboard/view/{id})
    const dashboardViewMatch = recentItem.url.match(/^\/dashboard\/view\/(\d+)/);
    if (dashboardViewMatch) {
      const dashboardId = Number(dashboardViewMatch[1]);

      // dashboardList와 featuredDashboardList에서 해당 id를 가진 대시보드 찾기
      const allDashboards = [...dashboardList];
      const foundDashboard = allDashboards.find(
        (dashboard) => dashboard.dashboardId === dashboardId,
      );

      if (foundDashboard && foundDashboard.dashboardName) {
        const path = appendQueryString(`/dashboard/view/${dashboardId}`, recentItem.queryString);
        const recentResultItem: MenuSearchRecentItem = {
          // 중복 대시보드 구분을 위해 index 포함
          id: `dashboard_${dashboardId}_recent_${index}`,
          // 공통 유틸리티 함수 사용 (dashboard 타입이므로 isDashboard: true)
          icon: getMenuSearchItemIcon({
            isDashboard: true,
            featuredIcon: foundDashboard.iconId as IconType | undefined,
          }),
          title: foundDashboard.dashboardName,
          featuredIcon: foundDashboard.iconId as IconType | undefined,
          subTitle: t ? t('WORD.DASHBOARD_VIEW') : '',
          path,
        };

        acc.push(recentResultItem);
      }

      return acc;
    }

    // path로 menuInfo에서 해당 item 찾기
    const foundMenuItem = findMenuInfoByPath(menuInfo, recentItem.url);

    if (foundMenuItem) {
      // 최상위 부모 찾기 (icon 추출용)
      const rootMenuItem = findRootMenuInfo(menuInfo, recentItem.url);
      const rootValue = rootMenuItem?.value || foundMenuItem.value;

      // EXCLUDED_MENU_VALUES에 포함된 메뉴는 제외
      if (EXCLUDED_MENU_VALUES.includes(rootValue)) {
        return acc;
      }

      const recentResultItem: MenuSearchRecentItem = {
        // 중복 메뉴 구분을 위해 index 포함
        id: `${foundMenuItem.value}_recent_${index}`,
        // 공통 유틸리티 함수 사용 (menu 타입이므로 isDashboard: false)
        icon: getMenuSearchItemIcon({
          isDashboard: false,
          rootValue,
        }),
        title: foundMenuItem.text,
        featuredIcon: foundMenuItem.featuredIcon as IconType | undefined,
        subTitle: lookupSubTitle(pathToSubTitleMap, foundMenuItem.path ?? recentItem.url),
        path: appendQueryString(foundMenuItem.path || recentItem.url, recentItem.queryString),
      };

      acc.push(recentResultItem);
    }

    return acc;
  }, []);
};

/**
 * 즐겨찾기 대시보드 데이터를 MenuSearchFavoriteDashboardItem 배열로 변환
 * lastModified가 1년 이내인 데이터만 필터링
 * 중복된 대시보드를 구분하기 위해 index를 포함한 unique id 생성
 * 최대 4개까지만 반환
 * @param dashboardList - 대시보드 목록 데이터
 * @returns MenuSearchFavoriteDashboardItem 배열 (최대 4개)
 */
export const mapFavoriteDashboardsToMenuItems = (
  dashboardList: (IntegratedDashboardV8Item & { lastModified?: string })[],
): MenuSearchFavoriteDashboardItem[] => {
  const today = getTzTimeNow();
  const MAX_FAVORITE_DASHBOARDS = 4;

  return dashboardList.reduce<MenuSearchFavoriteDashboardItem[]>((acc, dashboard, index) => {
    // 최대 개수에 도달하면 중단
    if (acc.length >= MAX_FAVORITE_DASHBOARDS) {
      return acc;
    }

    // dashboardId와 dashboardName이 있어야 함
    if (!dashboard.dashboardId || !dashboard.dashboardName) {
      return acc;
    }

    // favoriteId가 있어야 즐겨찾기 대시보드
    if (!dashboard.favoriteId) {
      return acc;
    }

    // lastModified가 있으면 1년(365일) 이내 데이터만 필터링
    if (dashboard.lastModified) {
      const lastModifiedDate = dayjs(dashboard.lastModified);

      // 유효한 날짜인지 확인
      if (lastModifiedDate.isValid()) {
        // 1년(365일) 이상 차이나면 제외
        if (isDifferenceMoreThanNDays(today, lastModifiedDate, 365)) {
          return acc;
        }
      }
    }

    const dashboardItem: MenuSearchFavoriteDashboardItem = {
      // 중복 대시보드 구분을 위해 index 포함
      id: `dashboard_${dashboard.dashboardId}_favorite_${index}`,
      // 공통 유틸리티 함수 사용 (dashboard 타입이므로 isDashboard: true)
      icon: getMenuSearchItemIcon({
        isDashboard: true,
      }),
      dashboardId: dashboard.dashboardId,
      favoriteId: dashboard.favoriteId,
      featuredIcon: dashboard.iconId as IconType | undefined,
      title: dashboard.dashboardName,
      dashboardType: dashboard.iconId ? 'preset' : 'custom',
      path: `/dashboard/view/${dashboard.dashboardId}`,
    };

    acc.push(dashboardItem);

    return acc;
  }, []);
};
